"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Ota() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const rows = [{
    eyebrow: "Channel Strategy",
    title: "Optimized OTA presence",
    body: "We curate your property listings across all major platforms to ensure maximum visibility and conversion. Our team manages content, imagery, and rate parity to keep your brand consistent and competitive in a crowded digital marketplace.",
    linkLabel: "View our strategy",
    linkTo: "/OTARevenueManagementListingsPage",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_dashboard_analytics.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_dashboard_analytics.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_dashboard_analytics.png-wvc-sizes",
      alt: "A digital dashboard showing hotel booking analytics"
    },
    reverse: false
  }, {
    eyebrow: "Revenue Control",
    title: "Dynamic rate management",
    body: "Stop losing margin to inefficient pricing. We implement automated yield management strategies that adjust your rates in real-time based on demand, competitor activity, and local market trends to maximize your RevPAR.",
    linkLabel: "Learn about revenue tools",
    linkTo: "/RevenueManagementConsultingPage",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png-wvc-sizes",
      alt: "Visual representation of hotel revenue growth charts"
    },
    reverse: true
  }, {
    eyebrow: "Direct Conversion",
    title: "Driving direct bookings",
    body: "While OTAs are essential, your website is your most profitable channel. We optimize your digital presence to capture high-intent traffic and convert lookers into bookers, reducing your reliance on third-party commissions.",
    linkLabel: "Explore our design services",
    linkTo: "/HighConvertingHotelWebsiteDesignOptimizationPage",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_booking_interface.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_booking_interface.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_booking_interface.png-wvc-sizes",
      alt: "A modern hotel booking interface on a laptop screen"
    },
    reverse: false
  }];
  return <section data-section-id="9821" id="ota-revenue-management-listings" className="bg-background text-foreground py-24 md:py-36">
      <div className="mx-auto max-w-[1200px] px-6 md:px-16">
        {}
        <div className="grid grid-cols-1 md:grid-cols-12">
          <div ref={intro.ref} className={`md:col-span-10 md:col-start-2 max-w-[680px] transition-all duration-[900ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <h2 className="font-default font-bold text-foreground" style={{
            fontSize: "clamp(2rem, 3.8vw, 3.25rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.018em"
          }}>
              Mastering your{" "}
              <em className={`not-italic font-default font-bold text-secondary transition-opacity duration-500 ease-out ${intro.isVisible ? "opacity-100 delay-200" : "opacity-0"}`}>
                OTA performance
              </em>
            </h2>
            <p className="mt-6 font-default text-foreground/80" style={{
            fontSize: "clamp(1.125rem, 1.3vw, 1.25rem)",
            lineHeight: 1.65,
            letterSpacing: "-0.005em"
          }}>
              We bridge the gap between visibility and profitability. Our data-driven approach ensures your property stands out on OTAs while protecting your direct booking margins.
            </p>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-[80px] flex flex-col gap-16 md:gap-24">
          {rows.map((row, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div key={i} data-index={i} ref={reveal.ref} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center transition-all duration-[900ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: reveal.isVisible ? `${i * 120}ms` : "0ms"
          }}>
                {}
                <div className={`md:col-span-6 order-2 ${row.reverse ? "md:order-2" : "md:order-1"}`}>
                  <p className="font-default font-semibold uppercase text-secondary" style={{
                fontSize: "clamp(0.75rem, 0.8vw, 0.875rem)",
                letterSpacing: "0.08em",
                lineHeight: 1.2
              }}>
                    {row.eyebrow}
                  </p>
                  <h3 className="mt-4 font-default font-bold text-foreground" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                lineHeight: 1.2,
                letterSpacing: "-0.012em"
              }}>
                    {row.title}
                  </h3>
                  <p className="mt-5 font-default text-foreground/70" style={{
                fontSize: "1rem",
                lineHeight: 1.7
              }}>
                    {row.body}
                  </p>
                  <div className="mt-7">
                    <Button asChild variant="link" className="h-auto p-0 text-secondary font-default font-semibold underline underline-offset-4 hover:underline-offset-[6px] transition-all duration-200">
                      <Link to={row.linkTo}>
                        <span>{row.linkLabel}</span>
                        <ArrowRight className="ml-1 size-4" strokeWidth={1.5} />
                      </Link>
                    </Button>
                  </div>
                </div>

                {}
                <div className={`md:col-span-6 order-1 ${row.reverse ? "md:order-1" : "md:order-2"}`}>
                  <div className="group overflow-hidden rounded-lg bg-card shadow-md hover:shadow-lg transition-shadow duration-[400ms] ease-out">
                    <div className="overflow-hidden rounded-lg">
                      <img src={row.image.src} data-wvc-srcset={row.image.srcset} data-wvc-sizes={row.image.sizes} alt={row.image.alt} loading="lazy" decoding="async" className="block w-full h-full aspect-[4/3] object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.02]" />
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}